<template>
  <div class="korean-label-edit">
    <h3 class="section-title">한글 표시사항 작업</h3>

    <!-- 로딩 중일 때 표시 -->
    <div v-if="isProcessing" class="loading-container">
      <b-spinner variant="primary"></b-spinner>
      <p>{{ progressMessage || '한글 표시사항 작업 중...' }}</p>
    </div>

    <!-- 작업 완료 후 표시 -->
    <div v-else-if="currentKoreanLabelImage" class="completed-work-container">
      <h4>한글 표시사항 정보</h4>

      <div class="card mb-4">
        <div class="card-header bg-light d-flex justify-content-between align-items-center">
          <span>표시 정보 결과
            <span v-if="selectedOption" class="text-muted">
              ({{ selectedOption.korName || selectedOption.optionNumber }})
            </span>
          </span>
          <div>
            <b-button variant="outline-primary" size="sm" @click="downloadLabelImage">
              <i class="las la-download"></i> 이미지 다운로드
            </b-button>
            <b-button variant="outline-secondary" size="sm" @click="resetLabels" class="ml-2">
              <i class="las la-edit"></i> 재작성
            </b-button>
          </div>
        </div>
        <div class="card-body">
          <img :src="currentKoreanLabelImage" alt="한글 표시사항" class="img-fluid">
        </div>
      </div>
    </div>

    <!-- 초기 상태 (작업 전) -->
    <div v-else class="initial-state-container">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <p class="mb-0">
          제품에 필요한 한글 표시사항을 작성해야 합니다. 항목을 자유롭게 추가, 수정, 삭제할 수 있습니다.
        </p>
        <div>
          <b-button variant="outline-info" size="sm" @click="resetToDefault" class="mr-2">
            <i class="las la-undo"></i> 기본값 복원
          </b-button>
          <b-button variant="outline-secondary" size="sm" @click="clearAll">
            <i class="las la-trash"></i> 전체 삭제
          </b-button>
        </div>
      </div>

      <div class="options-container">
        <div class="manual-input-container p-4">
          <!-- 동적 라벨 항목들 -->
          <div class="label-items-container">
            <draggable
              v-model="labelItems"
              @start="drag=true"
              @end="drag=false"
              handle=".drag-handle"
              class="list-group"
            >
              <div
                v-for="(item, index) in labelItems"
                :key="item.id"
                class="label-item-row mb-3"
                :class="{ 'item-changed': item.changed }"
              >
                <div class="d-flex align-items-center">
                  <!-- 드래그 핸들 -->
                  <div class="drag-handle mr-2">
                    <i class="las la-grip-vertical text-muted"></i>
                  </div>

                  <!-- 라벨 편집 -->
                  <div class="label-section mr-3" style="min-width: 150px;">
                    <div v-if="!item.editingLabel" class="label-display">
                      <span
                        class="label-text"
                        @click="startEditLabel(index)"
                        :class="{ 'required': item.required }"
                      >
                        {{ item.label }}
                        <span v-if="item.required" class="text-danger">*</span>
                      </span>
                      <small class="edit-hint text-muted ml-1">(클릭하여 편집)</small>
                    </div>
                    <div v-else class="label-edit">
                      <input
                        v-model="item.tempLabel"
                        @keyup.enter="saveLabel(index)"
                        @keyup.esc="cancelEditLabel(index)"
                        @blur="saveLabel(index)"
                        class="form-control form-control-sm"
                        ref="labelInput"
                        placeholder="항목명을 입력하세요"
                      />
                    </div>
                  </div>

                  <!-- 값 입력 -->
                  <div class="value-section flex-grow-1 mr-2">
                    <input
                      v-model="item.value"
                      @input="markAsChanged(index)"
                      type="text"
                      class="form-control"
                      :placeholder="`${item.label}을(를) 입력하세요`"
                    />
                  </div>

                  <!-- 액션 버튼들 -->
                  <div class="action-buttons">
                    <b-button
                      variant="outline-danger"
                      size="sm"
                      @click="removeItem(index)"
                      v-b-tooltip.hover
                      title="항목 삭제"
                    >
                      <i class="las la-times"></i>
                    </b-button>
                  </div>
                </div>
              </div>
            </draggable>

            <!-- 새 항목 추가 버튼 -->
            <div class="add-item-section mt-3">
              <b-button
                variant="outline-success"
                @click="addNewItem"
                class="w-100"
                style="border-style: dashed;"
              >
                <i class="las la-plus"></i> 새 항목 추가
              </b-button>
            </div>
          </div>

          <!-- 액션 버튼들 -->
          <div class="action-button-container text-center mt-4 d-flex justify-content-center">
            <b-button variant="success" class="mr-3" @click="saveManualLabels" :disabled="isProcessing">
              <i class="las la-save"></i> 정보 저장하기
            </b-button>
            <b-button variant="primary" @click="startKoreanLabelProcessing" :disabled="isProcessing">
              <i class="las la-language"></i> 한글 표시사항 생성 시작
            </b-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex';
import koreanLabelService, { DEFAULT_KOREAN_LABEL_ITEMS } from '../../services/works/koreanLabelService';
import { migrateLegacyLabels, migrateLegacyStructure } from '../../services/works/koreanLabelFields';
import draggable from 'vuedraggable';
import confirmInPageMixin from '@/mixins/confirmInPageMixin';

// 기본 항목 정의는 koreanLabelService 의 DEFAULT_KOREAN_LABEL_ITEMS 한 곳
const createDefaultLabelItems = () => DEFAULT_KOREAN_LABEL_ITEMS.map(item => ({
  ...item, value: '', changed: false, editingLabel: false, tempLabel: ''
}));

export default {
  mixins: [confirmInPageMixin],
  name: "KoreanLabelEdit",
  components: {
    draggable
  },
  data() {
    return {
      // 로딩 상태
      loading: false,
      drag: false,
      // 동적 라벨 항목들
      labelItems: createDefaultLabelItems(),
      // 기존 필드들 (호환성을 위해 유지)
      productName: '',
      importer: '',
      manufacturer: '',
      madein: '',
      content: '',
      material: '',
      type: '',
      caution: '',
      standard: '',
    };
  },
  computed: {
    // Vuex 상태 매핑
    ...mapState({
      selectedProduct: state => state.selectedRocketProduct,
      selectedOption: state => state.selectedOption,
      defaultSettings: state => state.settings[0]?.defaultSettings,
      koreanLabelImage: state => state.selectedRocketProduct?.koreanLabelImage,
      isProcessing: state => state.processing,
      progressMessage: state => state.progressMessage,
    }),

    // 현재 옵션의 한글 표시사항 이미지 (selectedOption 기준)
    currentKoreanLabelImage() {
      // 강제 반응성을 위해 여러 상태를 명시적으로 참조
      const productImage = this.selectedProduct?.koreanLabelImage;
      const optionImage = this.selectedOption?.koreanLabelImage;
      const productStatus = this.selectedProduct?.koreanLabelStatus;
      const optionStatus = this.selectedOption?.koreanLabelStatus;

      // 상태가 초기화되었으면 null 반환
      if (!productStatus && !optionStatus) {
        return null;
      }

      // 선택된 옵션이 있고 옵션별 이미지가 있는 경우
      if (this.selectedOption && optionImage) {
        return optionImage;
      }

      // 선택된 옵션이 없거나 옵션별 이미지가 없는 경우 상품 레벨 이미지 사용
      return productImage || null;
    }
  },
  watch: {
    // 선택된 옵션 변경 감시 (옵션별 한글 표시사항 표시)
    selectedOption: {
      handler(newOption) {
        if (newOption?.koreanLabels) {
          console.log('선택된 옵션 변경 - 한글 표시사항 업데이트:', newOption.optionNumber, newOption.koreanLabels);
          this.updateLabelItemsFromData(newOption.koreanLabels);
        } else if (this.selectedProduct?.koreanLabels) {
          // 옵션별 한글 표시사항이 없으면 상품 레벨 사용
          console.log('옵션별 한글 표시사항 없음 - 상품 레벨 사용');
          this.updateLabelItemsFromData(this.selectedProduct.koreanLabels);
        }
        // 강제 업데이트
        this.$forceUpdate();
      },
      immediate: true
    },
    // 선택된 상품의 koreanLabels 데이터 변경 감시 (폴백용)
    'selectedProduct.koreanLabels': {
      handler(newLabels) {
        // 옵션별 한글 표시사항이 없는 경우에만 상품 레벨 사용
        if (newLabels && !this.selectedOption?.koreanLabels) {
          console.log('상품 레벨 한글 라벨 데이터 변경 감지:', newLabels);
          this.updateLabelItemsFromData(newLabels);
        }
        // 강제 업데이트
        this.$forceUpdate();
      },
      deep: true,
      immediate: true
    },
    // 한글 표시사항 상태 변경 감시 (재작성 후 반응성 개선)
    'selectedProduct.koreanLabelStatus': {
      handler(newStatus, oldStatus) {
        console.log('한글 표시사항 상태 변경:', oldStatus, '→', newStatus);
        this.$forceUpdate();
      },
      immediate: true
    },
    // 한글 표시사항 이미지 변경 감시
    'selectedProduct.koreanLabelImage': {
      handler(newImage, oldImage) {
        console.log('한글 표시사항 이미지 변경:', oldImage, '→', newImage);
        this.$forceUpdate();
      },
      immediate: true
    },
    // labelItems 변경 시 기존 필드들도 동기화
    labelItems: {
      handler() {
        this.syncLegacyFields();
      },
      deep: true
    }
  },
  mounted() {
    this.initializeData();
  },
  methods: {
    ...mapActions([
      'updateRocketProduct',
      'setProcessing',
      'requestKoreanLabelData',
    ]),
    ...mapMutations([
      'SET_SELECTED_ROCKET_PRODUCT',
    ]),
    initializeData() {
      if (this.selectedProduct) {
        // 저장된 커스텀 라벨 구조가 있으면 사용
        if (this.selectedProduct.koreanLabelStructure) {
          this.labelItems = migrateLegacyStructure(this.selectedProduct.koreanLabelStructure).map(structure => ({
            ...structure,
            value: '',
            changed: false,
            editingLabel: false,
            tempLabel: ''
          }));
        }

        // 기본값 설정
        const defaultValues = {
          productName: this.selectedProduct.seo?.productName || this.selectedProduct.productName || '',
          importer: this.defaultSettings?.importer || '',
          manufacturer: this.selectedProduct.manufacturer || this.defaultSettings?.manufacturer || '',
          madein: '중국',
          caution: '용도 외에 사용금지. 파손및화기주의',
          standard: '14세이상',
          content: '',
          material: '',
          type: ''
        };

        // 기존 한글 표시사항 정보가 있으면 덮어쓰기
        if (this.selectedProduct.koreanLabels) {
          Object.assign(defaultValues, migrateLegacyLabels(this.selectedProduct.koreanLabels, this.selectedProduct.koreanLabelStructure));
        }

        // labelItems 업데이트
        this.labelItems.forEach(item => {
          if (defaultValues[item.id] !== undefined) {
            item.value = defaultValues[item.id];
          }
        });

        // 기존 필드들도 동기화
        this.syncLegacyFields();
      }
    },

    // 새로운 메서드들 추가
    updateLabelItemsFromData(rawLabels) {
      // 예전 편집 화면 꼴(manufacturer=수입 및 판매원, origin=제조원)으로 저장된 데이터는 정본 칸으로 옮겨 읽는다
      const newLabels = migrateLegacyLabels(rawLabels, this.selectedProduct?.koreanLabelStructure);
      this.labelItems.forEach(item => {
        if (newLabels[item.id] && item.value !== newLabels[item.id]) {
          item.value = newLabels[item.id];
          console.log(`${item.label} 업데이트:`, item.value);
        }
      });
    },

    syncLegacyFields() {
      // labelItems의 값을 기존 필드들에 동기화
      this.labelItems.forEach(item => {
        if (this[item.id] !== undefined) {
          this[item.id] = item.value;
        }
      });
    },

    markAsChanged(index) {
      this.labelItems[index].changed = true;
    },

    startEditLabel(index) {
      this.labelItems[index].tempLabel = this.labelItems[index].label;
      this.labelItems[index].editingLabel = true;
      this.$nextTick(() => {
        const inputs = this.$refs.labelInput;
        if (inputs && inputs[index]) {
          inputs[index].focus();
        }
      });
    },

    saveLabel(index) {
      if (this.labelItems[index].tempLabel.trim()) {
        this.labelItems[index].label = this.labelItems[index].tempLabel.trim();
        this.labelItems[index].changed = true;
      }
      this.cancelEditLabel(index);
    },

    cancelEditLabel(index) {
      this.labelItems[index].editingLabel = false;
      this.labelItems[index].tempLabel = '';
    },

    async removeItem(index) {
      if (this.labelItems[index].required) {
        alert('필수 항목은 삭제할 수 없습니다.');
        return;
      }

      if (await this.confirmInPage(`"${this.labelItems[index].label}" 항목을 삭제하시겠습니까?`)) {
        this.labelItems.splice(index, 1);
      }
    },

    addNewItem() {
      const newId = 'custom_' + Date.now();
      const newItem = {
        id: newId,
        label: '새 항목',
        value: '',
        required: false,
        changed: true,
        editingLabel: true,
        tempLabel: '새 항목'
      };

      this.labelItems.push(newItem);

      // 새 항목의 라벨 편집 모드로 전환
      this.$nextTick(() => {
        const inputs = this.$refs.labelInput;
        if (inputs && inputs.length > 0) {
          const lastInput = inputs[inputs.length - 1];
          if (lastInput) {
            lastInput.focus();
            lastInput.select();
          }
        }
      });
    },

    async resetToDefault() {
      if (await this.confirmInPage('모든 항목을 기본값으로 복원하시겠습니까?')) {
        // 기본 항목들로 재설정
        this.labelItems = createDefaultLabelItems();
        this.initializeData();
      }
    },

    async clearAll() {
      if (await this.confirmInPage('모든 항목을 삭제하시겠습니까? (필수 항목 제외)')) {
        this.labelItems = this.labelItems.filter(item => item.required);
        this.labelItems.forEach(item => {
          item.value = '';
          item.changed = true;
        });
      }
    },

    // 수정: 수동으로 입력한 정보 저장
    async saveManualLabels() {
      try {
        // 로딩 상태 시작
        this.setProcessing({
          isProcessing: true,
          message: '한글 표시사항 저장 중...'
        });

        // 라벨 정보 객체 생성 (동적 라벨 항목들로부터)
        const labels = {};
        this.labelItems.forEach(item => {
          labels[item.id] = item.value;
        });

        // 커스텀 라벨 구조도 저장
        const customLabelStructure = this.labelItems.map(item => ({
          id: item.id,
          label: item.label,
          required: item.required
        }));

        // 항상 새로운 이미지 생성 (수동 저장 시 변경된 내용 반영)
        console.log('🖼️ 새로운 한글 표시사항 이미지 생성 중...');
        const imageBase64 = await koreanLabelService.generateLabelImage(labels, customLabelStructure);
        const imageResult = await koreanLabelService.uploadLabelImage(this.selectedProduct, imageBase64);
        const imageUrl = imageResult.imageUrl;
        console.log('✅ 새로운 이미지 생성 완료:', imageUrl);

        // 변경 상태 초기화
        this.labelItems.forEach(item => {
          item.changed = false;
        });

        // 점 표기법을 사용하여 업데이트할 필드 지정
        const updateFields = {
          'koreanLabels': labels,
          'koreanLabelStructure': customLabelStructure,
          'koreanLabelImage': imageUrl,
          'koreanLabelStatus': 'completed',
          'updatedAt': Date.now()
        };

        // 옵션별 한글 표시사항 정보 업데이트 (점표기법)
        if (this.selectedProduct.options && this.selectedProduct.options.length > 0) {
          this.selectedProduct.options.forEach((option, optionIndex) => {
            // 각 옵션에 한글 표시사항 정보 저장
            updateFields[`options.${optionIndex}.koreanLabels`] = labels;
            updateFields[`options.${optionIndex}.koreanLabelImage`] = imageUrl;
            updateFields[`options.${optionIndex}.koreanLabelStatus`] = 'completed';
          });
        }

        // coupangDoc 업데이트 (점표기법으로 모든 옵션에 적용)
        if (this.selectedProduct.coupangDoc && this.selectedProduct.coupangDoc.jsonDocument) {
          console.log('📄 coupangDoc 업데이트 시작...');

          this.selectedProduct.coupangDoc.jsonDocument.forEach((docItem, docIndex) => {
            if (docItem.imagePage && docItem.imagePage.images) {
              // labelImages 배열이 없으면 빈 배열로 초기화
              if (!docItem.imagePage.images.labelImages) {
                updateFields[`coupangDoc.jsonDocument.${docIndex}.imagePage.images.labelImages`] = [];
              }

              // 한글 표시사항 라벨 이미지 찾기
              const existingLabelImages = docItem.imagePage.images.labelImages || [];
              const labelImageIndex = existingLabelImages.findIndex(
                img => img.labelImageType === '제품 한글 표시사항 라벨 또는 도안 이미지'
              );

              if (labelImageIndex !== -1) {
                // 기존 이미지 업데이트 (점표기법)
                updateFields[`coupangDoc.jsonDocument.${docIndex}.imagePage.images.labelImages.${labelImageIndex}.labelImageFiles`] = imageUrl;
                console.log(`✅ 옵션 ${docIndex} 기존 한글표시사항 이미지 업데이트`);
              } else {
                // 새 이미지 추가 - 전체 배열 업데이트 방식
                const updatedLabelImages = [...existingLabelImages, {
                  labelImageType: '제품 한글 표시사항 라벨 또는 도안 이미지',
                  labelImageFiles: imageUrl
                }];
                updateFields[`coupangDoc.jsonDocument.${docIndex}.imagePage.images.labelImages`] = updatedLabelImages;
                console.log(`✅ 옵션 ${docIndex} 새로운 한글표시사항 이미지 추가`);
              }
            }
          });
        }

        // 서버에 부분 업데이트 요청
        console.log('💾 서버 및 Vuex 상태 업데이트 시작...');
        console.log('📋 업데이트 필드 목록:', Object.keys(updateFields));

        await this.$store.dispatch('updateProductPartial', {
          productId: this.selectedProduct._id,
          updateFields: updateFields
        });

        console.log('✅ 한글 표시사항 저장 완료');
        this.setProcessing({ isProcessing: false });

        // 성공 메시지 표시
        this.$bvToast.toast('한글 표시사항이 저장되었습니다.', {
          title: '성공',
          variant: 'success',
          solid: true,
          toaster: 'b-toaster-bottom-right'
        });

      } catch (error) {
        console.error('❌ 한글 표시사항 저장 중 오류:', error);
        this.setProcessing({ isProcessing: false });

        // 에러 메시지 표시
        this.$bvToast.toast('한글 표시사항 저장 중 오류가 발생했습니다.', {
          title: '오류',
          variant: 'danger',
          solid: true,
          toaster: 'b-toaster-bottom-right'
        });
      }
    },

    // 한글 표시사항 작업 시작
    async startKoreanLabelProcessing() {
      try {
        // 로딩 상태 시작
        this.setProcessing({
          isProcessing: true,
          message: '한글 표시사항 생성 요청 중...'
        });

        // Vuex 액션을 직접 호출
        await this.requestKoreanLabelData({
          product: this.selectedProduct,
        });
      } catch (error) {
        console.error('한글 표시사항 생성 중 오류:', error);
        alert('한글 표시사항 생성 중 오류가 발생했습니다.');
      } finally {
        // 성공·실패 모두 로딩을 끝낸다 — 안 끄면 버튼이 계속 막힌다
        this.setProcessing({ isProcessing: false });
      }
    },
    downloadLabelImage() {
      const link = document.createElement('a');
      link.href = this.currentKoreanLabelImage;
      link.download = `korean_label_${this.selectedOption?.optionNumber || 'product'}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    // 이미지 초기화
    async resetLabels() {
      // 브라우저 기본 confirm 은 탭 JS 를 멈춰 외부 자동화(CDP)가 시간 초과된다 → 화면 안 모달 사용
      const confirmed = await this.$bvModal.msgBoxConfirm('이미지를 재작성 하시겠습니까?', {
        title: '확인',
        okTitle: '재작성',
        cancelTitle: '취소',
        centered: true
      });
      if (confirmed) {
        try {
          console.log('🔄 한글 표시사항 재작성 시작...');

          // 로딩 상태 시작
          this.setProcessing({
            isProcessing: true,
            message: '한글 표시사항 초기화 중...'
          });

          // 점 표기법을 사용하여 업데이트할 필드 지정
          const updateFields = {
            'koreanLabelImage': null,
            'koreanLabelStatus': null,
            'updatedAt': Date.now()
          };

          // 옵션별 상태도 초기화
          if (this.selectedProduct.options && this.selectedProduct.options.length > 0) {
            this.selectedProduct.options.forEach((option, index) => {
              updateFields[`options.${index}.koreanLabelImage`] = null;
              updateFields[`options.${index}.koreanLabelStatus`] = null;
            });
          }

          // 서버에 부분 업데이트 요청
          await this.$store.dispatch('updateProductPartial', {
            productId: this.selectedProduct._id,
            updateFields: updateFields
          });

          console.log('✅ 한글 표시사항 초기화 완료');
          this.setProcessing({ isProcessing: false });

          // Vue 반응성 강제 업데이트
          this.$forceUpdate();

          // 다음 틱에서 데이터 재초기화
          this.$nextTick(() => {
            this.initializeData();
            this.$forceUpdate();
          });

          // 성공 메시지 표시
          this.$bvToast.toast('한글 표시사항이 초기화되었습니다. 다시 생성할 수 있습니다.', {
            title: '초기화 완료',
            variant: 'success',
            solid: true,
            toaster: 'b-toaster-bottom-right'
          });

        } catch (error) {
          console.error('❌ 한글 표시사항 초기화 중 오류:', error);
          this.setProcessing({ isProcessing: false });

          // 에러 메시지 표시
          this.$bvToast.toast('한글 표시사항 초기화 중 오류가 발생했습니다.', {
            title: '오류',
            variant: 'danger',
            solid: true,
            toaster: 'b-toaster-bottom-right'
          });
        }
      }
    },
    // 다음 단계로 이동
    // goToNextStep() {
    //   this.$emit('next');
    // }
  }
}
</script>

<style scoped>
.korean-label-edit {
  max-width: 100%;
}

.label-item-row {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 15px;
  transition: all 0.3s ease;
}

.label-item-row:hover {
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.label-item-row.item-changed {
  border-left: 4px solid #28a745;
  background: #f8fff9;
}

.drag-handle {
  cursor: grab;
  padding: 5px;
  border-radius: 4px;
}

.drag-handle:hover {
  background: #e9ecef;
}

.drag-handle:active {
  cursor: grabbing;
}

.label-section {
  display: flex;
  flex-direction: column;
}

.label-display {
  display: flex;
  align-items: center;
  min-height: 38px;
}

.label-text {
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 4px;
  background: #fff;
  border: 1px solid #dee2e6;
  transition: all 0.2s ease;
  font-weight: 500;
}

.label-text:hover {
  background: #e9ecef;
  border-color: #adb5bd;
}

.label-text.required {
  background: #fff3cd;
  border-color: #ffeaa7;
}

.edit-hint {
  font-size: 0.75rem;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.label-display:hover .edit-hint {
  opacity: 1;
}

.value-section input {
  transition: border-color 0.2s ease;
}

.value-section input:focus {
  border-color: #80bdff;
  box-shadow: 0 0 0 0.2rem rgba(0,123,255,.25);
}

.action-buttons {
  display: flex;
  gap: 5px;
}

.add-item-section {
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  transition: all 0.2s ease;
}

.add-item-section:hover {
  border-color: #28a745;
  background: #f8fff9;
}

.loading-container {
  text-align: center;
  padding: 40px;
}

.completed-work-container img {
  max-width: 100%;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

/* 드래그 중일 때 스타일 */
.sortable-ghost {
  opacity: 0.5;
}

.sortable-chosen {
  transform: scale(1.02);
}

/* 반응형 디자인 */
@media (max-width: 768px) {
  .label-item-row .d-flex {
    flex-direction: column;
    gap: 10px;
  }

  .label-section {
    min-width: auto !important;
    width: 100%;
  }

  .value-section {
    width: 100%;
  }

  .action-buttons {
    justify-content: center;
  }

  .edit-hint {
    display: none;
  }
}

/* 애니메이션 */
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}

.label-item-row {
  animation: fadeIn 0.3s ease;
}
</style>